import React, { useState, useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import TextAlign from "@tiptap/extension-text-align";
import Underline from "@tiptap/extension-underline";
import TextStyle from "@tiptap/extension-text-style";
import Highlight from "@tiptap/extension-highlight";
import Color from "@tiptap/extension-color";
import FontFamily from "@tiptap/extension-font-family";
import PropTypes from "prop-types";
import { useNavigate, useLocation } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getFirestore, doc, updateDoc } from "firebase/firestore";
import {
  getStorage,
  ref,
  uploadBytesResumable,
  getDownloadURL,
} from "firebase/storage";
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  Heading1,
  Heading2,
  Image as ImageIcon,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Highlighter,
  ImageDown,
  X,
  Paintbrush,
} from "lucide-react";
import { useParams } from "react-router-dom";
const MenuBar = ({ editor }) => {
  const [imageUrl, setImageUrl] = useState("");
  const [showImageModal, setShowImageModal] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!showImageModal) {
      setImageUrl("");
      setPreviewUrl("");
      setError("");
    }
  }, [showImageModal]);

  const validateAndPreviewImage = (url) => {
    setImageUrl(url);
    setError("");
    if (!url) return setPreviewUrl("");
    const img = new Image();
    img.onload = () => setPreviewUrl(url);
    img.onerror = () => {
      setPreviewUrl("");
      setError("Invalid image URL or failed to load.");
    };
    img.src = url;
  };

  const addImage = () => {
    if (imageUrl && !error) {
      editor.chain().focus().setImage({ src: imageUrl }).run();
      setShowImageModal(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file?.type.startsWith("image/")) return setError("Invalid file type.");
    const storage = getStorage();
    const storageRef = ref(storage, `images/${file.name}`);
    const uploadTask = uploadBytesResumable(storageRef, file);

    setLoading(true);
    uploadTask.on(
      "state_changed",
      null,
      (error) => {
        setError("Upload failed: " + error.message);
        setLoading(false);
      },
      () => {
        getDownloadURL(uploadTask.snapshot.ref).then((url) => {
          setPreviewUrl(url);
          editor.chain().focus().setImage({ src: url }).run();
          setLoading(false);
        });
      }
    );
  };

  if (!editor) return null;

  return (
    <>
      <div className="sticky top-28 left-0 z-10 flex flex-col flex-wrap max-h-[80vh] overflow-y-auto gap-2 p-2 bg-gray-100 border-r border-gray-300">
        {/* Styling Buttons */}
        <button
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`p-2 rounded hover:bg-gray-200 ${
            editor.isActive("bold") ? "bg-gray-300" : ""
          }`}
        >
          <Bold className="w-4 h-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`p-2 rounded hover:bg-gray-200 ${
            editor.isActive("italic") ? "bg-gray-300" : ""
          }`}
        >
          <Italic className="w-4 h-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={`p-2 rounded hover:bg-gray-200 ${
            editor.isActive("underline") ? "bg-gray-300" : ""
          }`}
        >
          <UnderlineIcon className="w-4 h-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleHighlight().run()}
          className={`p-2 rounded hover:bg-gray-200 ${
            editor.isActive("highlight") ? "bg-yellow-200" : ""
          }`}
        >
          <Highlighter className="w-4 h-4" />
        </button>
        <button
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 1 }).run()
          }
          className={`p-2 rounded hover:bg-gray-200 ${
            editor.isActive("heading", { level: 1 }) ? "bg-gray-300" : ""
          }`}
        >
          <Heading1 className="w-4 h-4" />
        </button>
        <button
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 2 }).run()
          }
          className={`p-2 rounded hover:bg-gray-200 ${
            editor.isActive("heading", { level: 2 }) ? "bg-gray-300" : ""
          }`}
        >
          <Heading2 className="w-4 h-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().setTextAlign("left").run()}
          className={`p-2 rounded hover:bg-gray-200 ${
            editor.isActive({ textAlign: "left" }) ? "bg-gray-300" : ""
          }`}
        >
          <AlignLeft className="w-4 h-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().setTextAlign("center").run()}
          className={`p-2 rounded hover:bg-gray-200 ${
            editor.isActive({ textAlign: "center" }) ? "bg-gray-300" : ""
          }`}
        >
          <AlignCenter className="w-4 h-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().setTextAlign("right").run()}
          className={`p-2 rounded hover:bg-gray-200 ${
            editor.isActive({ textAlign: "right" }) ? "bg-gray-300" : ""
          }`}
        >
          <AlignRight className="w-4 h-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`p-2 rounded hover:bg-gray-200 ${
            editor.isActive("bulletList") ? "bg-gray-300" : ""
          }`}
        >
          <List className="w-4 h-4" />
        </button>
        <label className="p-2 rounded hover:bg-gray-200 cursor-pointer">
          <Paintbrush className="w-4 h-4" />
          <input
            type="color"
            className="hidden"
            onChange={(e) =>
              editor.chain().focus().setColor(e.target.value).run()
            }
          />
        </label>
        <select
          onChange={(e) =>
            editor.chain().focus().setFontFamily(e.target.value).run()
          }
          className="text-xs rounded p-1"
          defaultValue="inherit"
        >
          <option value="inherit">Default</option>
          <option value="Georgia, serif">Georgia</option>
          <option value="Arial, sans-serif">Arial</option>
          <option value="'Courier New', monospace">Courier New</option>
        </select>
        <button
          onClick={() => setShowImageModal(true)}
          className="p-2 rounded hover:bg-gray-200"
        >
          <ImageIcon className="w-4 h-4" />
        </button>
        <label className="p-2 rounded hover:bg-gray-200 cursor-pointer">
          <ImageDown className="w-4 h-4" />
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleImageUpload}
          />
        </label>
      </div>

      {/* Image Modal */}
      {showImageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow max-w-lg w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Insert Image</h3>
              <button
                onClick={() => setShowImageModal(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <input
              type="text"
              value={imageUrl}
              onChange={(e) => validateAndPreviewImage(e.target.value)}
              placeholder="Paste image URL..."
              className="w-full border p-2 rounded mb-2"
            />
            {error && <p className="text-red-500">{error}</p>}
            {previewUrl && (
              <img
                src={previewUrl}
                alt="Preview"
                className="max-h-60 mx-auto my-2"
              />
            )}
            <div className="flex justify-end">
              <button
                onClick={addImage}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                Insert
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

MenuBar.propTypes = {
  editor: PropTypes.object,
};

const TextEdit = () => {
  const location = useLocation();
  const post = location.state?.post;

  // Ensure title and content are available if the post is passed
  const [title, setTitle] = useState(post?.title || "");
  const [content, setContent] = useState(
    post?.content || "<p>Hello World!</p>"
  );
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const navigate = useNavigate();
  const auth = getAuth();
  const db = getFirestore();

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      if (u) setUser(u);
      else navigate("/login?redirect=blog-editor");
    });
    return unsub;
  }, [auth, navigate]);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Image,
      Underline,
      Highlight,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      TextStyle,
      Color,
      FontFamily,
    ],
    content,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      setContent(html);
      localStorage.setItem("editorContent", html);
    },
  });

  const { id } = useParams(); // This will get the `id` from the URL

  const handleUpdate = async () => {
    if (!user) return navigate("/login?redirect=blog-editor");
    if (!title.trim()) return alert("Title is required!");
    if (!id) return alert("Missing post ID."); // Ensure ID exists from URL

    setLoading(true);
    setProgress(0);

    try {
      for (let i = 0; i <= 100; i += 10) {
        await new Promise((res) => setTimeout(res, 100));
        setProgress(i);
      }

      const postRef = doc(db, "posts", id); // Ensure this is using `id` from the URL
      await updateDoc(postRef, {
        title,
        content,
        updatedAt: new Date(),
      });

      alert("Blog post updated!");
      navigate(`/blog/${id}`); // Navigate to the post using its ID
    } catch (err) {
      console.error(err);
      alert("Failed to update blog post.");
    } finally {
      setLoading(false);
    }
  };

  // Render loading state if in progress
  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded-lg shadow-lg">
          <p className="text-lg font-semibold">Uploading...</p>
          <div className="mt-4 w-64">
            <div className="w-full bg-gray-200 rounded-full h-4">
              <div
                className="bg-blue-500 h-4 rounded-full"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-center mt-2">{progress}%</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 min-h-screen pt-28 px-2">
      <div className="w-full max-w-screen-xl mx-auto flex flex-row items-start gap-6">
        <div className="w-[50px] sm:w-[120px] md:w-[150px] flex-shrink-0 my-auto">
          <MenuBar editor={editor} />
        </div>

        <div className="flex-grow max-h-[80vh] overflow-y-auto rounded-lg p-2">
          <div className="mb-4">
            <label htmlFor="title" className="block text-lg font-semibold mb-2">
              Blog Title
            </label>
            <input
              id="title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full border px-3 py-2 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter blog title..."
            />
          </div>

          <EditorContent
            editor={editor}
            className="p-2 bg-white rounded shadow min-h-[200px]"
          />

          <div className="flex justify-end mt-4">
            <button
              onClick={handleUpdate}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 m-auto"
            >
              Update
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TextEdit;
